import { useEffect, useState } from "react";
import owasp from "owasp-password-strength-test";
import { passwordStrength } from "check-password-strength";
import generator from "generate-password";

const strengthsBackgrounds = {
  0: "bg-danger",
  1: "bg-warning",
  2: "bg-info",
  3: "bg-success",
};

const counts = [1, 2, 3, 4, 5, 10, 20, 25, 50, 100, 200, 500, 1000];

const PasswordsGeneratorOptions = (
  {
    options: {
      length = 20,
      symbols = true,
      numbers = true,
      strict = true,
      casing = "mixed",
      count = 20,
    } = {},
    onChange,
    strings,
    toolStrings,
  } = {
    options: {},
  }
) => {
  const allCases = [
    { name: "mixed", display: toolStrings.mixed },
    { name: "uppercase", display: toolStrings.uppercase },
    { name: "lowercase", display: toolStrings.lowercase },
  ];

  const strengthsTexts = {
    0: toolStrings.tooWeak,
    1: toolStrings.weak,
    2: toolStrings.medium,
    3: toolStrings.strong,
  };

  const [strength, setStrength] = useState(0);

  useEffect(() => {
    try {
      const passwords = generator.generateMultiple(40, {
        length,
        symbols,
        numbers,
        strict,
        casing,
        count,
      });

      owasp.config({
        allowPassphrases: true,
        maxLength: 1024,
        minLength: 9,
        minPhraseLength: 1024,
        minOptionalTestsToPass: 4,
      });

      /*
      // Full report
      use owasp to generate report
      const { optionalTestsPassed, errors, failedTests } = owasp.test(password);
      const hasMinLengthFail = failedTests.find((c) => c === 0) === 0;
      const finalResult = 1 + optionalTestsPassed - errors.length;
      */

      const strengths = passwords.map((p) => passwordStrength(p).id);
      const averageStrength =
        strengths.reduce((a, c) => a + c, 0) / strengths.length;

      setStrength(Math.ceil(averageStrength));
    } catch (_) {
      setStrength(0);
    }
  }, [length, symbols, numbers, strict, casing, count]);

  return (
    <>
      <div className={"row p-0 m-1 pb-2 d-flex"}>
        <div className="col col-4 col-md-2 col-xl-1">
          <label forhtml="lengthLabel">{strings.Length}</label>
          <input
            className="form-control"
            type="number"
            value={isNaN(length) ? "" : length}
            onChange={({ target: { value } }) =>
              onChange({
                length:
                  parseInt(value) < 0
                    ? 1
                    : parseInt(value) > 1023
                    ? 1024
                    : parseInt(value),
                symbols,
                count,
                strict,
                numbers,
                casing,
              })
            }
          />
        </div>
        <div className="col col-4 col-md-2 col-xl-2">
          <label forhtml="casingLabel ">{toolStrings.characterCase}</label>
          <select
            className="form-select"
            aria-label="type"
            onChange={({ target: { value } }) =>
              onChange({ casing: value, length, count, symbols })
            }
            value={casing}
          >
            {allCases.map(({ name, display }) => (
              <option key={name} value={name}>
                {display}
              </option>
            ))}
          </select>
        </div>
        <div className="col col-4 col-md-2 col-xl-1">
          <label forhtml="countLabel">{strings.Count}</label>
          <select
            className="form-select"
            onChange={({ target: { value } }) =>
              onChange({
                count: parseInt(value),
                length,
                casing,
                symbols,
                numbers,
                strict,
              })
            }
            value={count}
            aria-label="Count"
          >
            {counts.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="col col-4 col-md-2 col-xl-1">
          <label forhtml="symbols-label">{toolStrings.symbols}</label>
          <div className="mt-1" />
          <input
            id="symbols-label"
            className="form-check-input"
            type="checkbox"
            checked={symbols}
            onChange={({ target: { checked } }) =>
              onChange({
                symbols: checked,
                numbers,
                strict,
                length,
                count,
                casing,
              })
            }
          />
        </div>
        <div className="col col-4 col-md-2 col-xl-1">
          <label forhtml="numbers-label">{toolStrings.numbers}</label>
          <div className="mt-1" />
          <input
            id="numbers-label"
            className="form-check-input"
            type="checkbox"
            checked={numbers}
            onChange={({ target: { checked } }) =>
              onChange({
                numbers: checked,
                strict,
                symbols,
                length,
                count,
                casing,
              })
            }
          />
        </div>
        <div className="col col-4 col-md-2 col-xl-1">
          <label forhtml="strict-label">{toolStrings.strict}</label>
          <div className="mt-1" />
          <input
            id="strict-label"
            className="form-check-input"
            type="checkbox"
            checked={strict}
            onChange={({ target: { checked } }) =>
              onChange({
                strict: checked,
                numbers,
                symbols,
                length,
                count,
                casing,
              })
            }
          />
        </div>
      </div>
      <div className="row mt-1 mb-2 progress">
        <div
          className={`progress-bar  ${strengthsBackgrounds[strength]}`}
          role="progressbar"
          style={{ width: `${(strength + 1) * 25}%` }}
          aria-valuenow={(strength + 1) * 25}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {strengthsTexts[strength]}
        </div>
      </div>
    </>
  );
};

export default PasswordsGeneratorOptions;
